import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Avatar,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Collapse,
  Checkbox,
  Divider,
  Chip,
  Badge,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CategoryIcon from "@mui/icons-material/Category";
import { getCategories } from "../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../services/indexMediaBaseUrl";

const CategoriesHorizontallyCompo = ({ onSelectCategory }) => {
  const { t, i18n } = useTranslation("product_categories_component");
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selectedCatId, setSelectedCatId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getCategories();
        const list = res && res.success && Array.isArray(res.data) ? res.data : [];

        const childrenMap = list.reduce((acc, cat) => {
          const pid = cat.category_id;
          if (pid && cat.status !== 0) {
            if (!acc[pid]) acc[pid] = [];
            acc[pid].push(cat);
          }
          return acc;
        }, {});

        const parents = list.filter((c) => !c.category_id && c.status !== 0);

        const structured = parents.map((p) => {
          const children = (childrenMap[p.id] || []).map((child) => ({
            ...child,
            children: (childrenMap[child.id] || []).filter((gc) => gc.status !== 0),
          }));
          return { ...p, children };
        });

        setTree(structured);
      } catch (err) {
        setError(err?.message || t("error_loading_categories", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الفئات" : "Error loading categories" }));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (c) => (lang === "ar" ? c.name_ar || c.name_en : c.name_en || c.name_ar);
  const getDesc = (c) => (lang === "ar" ? c.desc_ar || c.desc_en : c.desc_en || c.desc_ar);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectCategory = (catId, hasChildren) => {
    setSelectedCatId(catId);
    if (typeof onSelectCategory === "function") {
      onSelectCategory(catId);
    }
    // إذا كانت الفئة لها أطفال، افتحها أيضاً
    if (hasChildren) {
      setExpanded((prev) => ({ ...prev, [catId]: true }));
    }
  };

  const countProducts = (category) => {
    let count = 0;
    if (category.products) count += category.products.length;
    if (category.children) {
      category.children.forEach((child) => {
        count += countProducts(child);
      });
    }
    return count;
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "primary.main" }} size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error" variant="body2" fontWeight={600}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 2,
        px: 0,
        bgcolor: "background.paper",
        borderRadius: 3,
        border: "1px solid rgba(213, 170, 47, 0.1)",
        boxShadow: "0 4px 16px rgba(213, 170, 47, 0.08)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background:
            "linear-gradient(90deg, #d5aa2f 0%, #e6c76a 50%, #d5aa2f 100%)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          pb: 2,
          mb: 2,
          borderBottom: "2px solid",
          borderImage: "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              background:
                "linear-gradient(135deg, rgba(213, 170, 47, 0.1) 0%, rgba(230, 199, 106, 0.1) 100%)",
              border: "1px solid rgba(213, 170, 47, 0.2)",
            }}
          >
            <CategoryIcon sx={{ color: "primary.main", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{
                background:
                  "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("categories", { defaultValue: lang === "ar" ? "الأقسام" : "Categories" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {tree.length} {lang === "ar" ? "قسم رئيسي" : "main categories"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* All categories button */}
      <Box sx={{ px: 1.5, mb: 1 }}>
        <ListItemButton
          onClick={() => {
            setSelectedCatId(null);
            if (typeof onSelectCategory === "function") onSelectCategory(null);
          }}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            fontWeight: 700,
            transition: "all 0.25s ease",
            bgcolor: selectedCatId === null ? "rgba(213,170,47,0.12)" : "transparent",
            borderLeft: selectedCatId === null ? "4px solid" : "4px solid transparent",
            borderColor: selectedCatId === null ? "primary.main" : "transparent",
            "&:hover": { bgcolor: "rgba(213,170,47,0.07)", transform: "translateX(4px)" },
          }}
        >
          <Typography variant="body2" fontWeight={selectedCatId === null ? 800 : 600} color={selectedCatId === null ? "primary.main" : "text.primary"}>
            {lang === "ar" ? "كل الأقسام" : "All Categories"}
          </Typography>
        </ListItemButton>
      </Box>

      {/* Tree List */}
      <List sx={{ px: 1, maxHeight: 560, overflow: "auto" }}>
        {tree.map((parent, pIdx) => (
          <Box key={parent.id}>
            {/* Parent Category */}
            <ListItem
              disablePadding
              sx={{
                mb: 1,
                animation: `fadeIn 0.4s ease-out ${pIdx * 0.1}s both`,
                "@keyframes fadeIn": {
                  from: { opacity: 0, transform: "translateX(-10px)" },
                  to: { opacity: 1, transform: "translateX(0)" },
                },
              }}
            >
              <ListItemButton
                onClick={(e) => {
                  // التحقق من النقر على expand icon أو باقي المنطقة
                  const clickedOnIcon = e.target.closest('.expand-icon');
                  
                  if (clickedOnIcon) {
                    // نقر على أيقونة التوسيع فقط
                    handleToggle(parent.id);
                  } else {
                    // نقر على أي مكان آخر - اختر الفئة
                    handleSelectCategory(parent.id, parent.children && parent.children.length > 0);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  transition: "all 0.3s ease",
                  bgcolor: selectedCatId === parent.id ? "rgba(213, 170, 47, 0.12)" : "transparent",
                  borderLeft: selectedCatId === parent.id ? "4px solid" : "4px solid transparent",
                  borderColor: selectedCatId === parent.id ? "primary.main" : "transparent",
                  "&:hover": {
                    bgcolor: "rgba(213, 170, 47, 0.08)",
                    transform: "translateX(4px)",
                    boxShadow: "0 2px 8px rgba(213, 170, 47, 0.1)",
                  },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 56 }}>
                  <Badge
                    badgeContent={countProducts(parent)}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: "primary.main",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                      },
                    }}
                  >
                    <Avatar
                      src={getImageUrl(parent.image)}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        border: "2px solid rgba(213, 170, 47, 0.2)",
                        boxShadow: "0 2px 8px rgba(213, 170, 47, 0.15)",
                      }}
                    />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      {getName(parent)}
                    </Typography>
                  }
                  secondary={
                    getDesc(parent) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {getDesc(parent)}
                      </Typography>
                    )
                  }
                />
                {parent.children && parent.children.filter((c) => c.status !== 0).length > 0 && (
                  <Box
                    className="expand-icon"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(parent.id);
                    }}
                  >
                    <Chip
                      label={parent.children.filter((c) => c.status !== 0).length}
                      size="small"
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "primary.dark",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        height: 22,
                        pointerEvents: "none",
                      }}
                    />
                    {expanded[parent.id] ? (
                      <ExpandLess sx={{ color: "primary.main", pointerEvents: "none" }} />
                    ) : (
                      <ExpandMore sx={{ color: "text.secondary", pointerEvents: "none" }} />
                    )}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>

            {/* Children Categories */}
            {parent.children && parent.children.filter((c) => c.status !== 0).length > 0 && (
              <Collapse in={expanded[parent.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {parent.children.filter((c) => c.status !== 0).map((child, cIdx) => (
                    <Box key={child.id}>
                      <ListItem
                        disablePadding
                        sx={{
                          pl: 3,
                          mb: 0.5,
                          animation: `slideIn 0.3s ease-out ${cIdx * 0.05}s both`,
                          "@keyframes slideIn": {
                            from: { opacity: 0, transform: "translateX(-10px)" },
                            to: { opacity: 1, transform: "translateX(0)" },
                          },
                        }}
                      >
                        <ListItemButton
                          onClick={(e) => {
                            const clickedOnIcon = e.target.closest('.expand-icon');
                            
                            if (clickedOnIcon) {
                              handleToggle(child.id);
                            } else {
                              handleSelectCategory(child.id, child.children && child.children.length > 0);
                            }
                          }}
                          sx={{
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                            transition: "all 0.3s ease",
                            bgcolor: selectedCatId === child.id ? "rgba(213, 170, 47, 0.1)" : "transparent",
                            borderLeft: selectedCatId === child.id ? "3px solid" : "3px solid transparent",
                            borderColor: selectedCatId === child.id ? "primary.light" : "transparent",
                            "&:hover": {
                              bgcolor: "rgba(213, 170, 47, 0.06)",
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 8,
                              color: "primary.main",
                              mr: 1,
                            }}
                          />
                          <ListItemAvatar sx={{ minWidth: 48 }}>
                            <Avatar
                              src={getImageUrl(child.image)}
                              variant="rounded"
                              sx={{
                                width: 36,
                                height: 36,
                                border: "1px solid rgba(213, 170, 47, 0.2)",
                              }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={600} color="text.primary">
                                {getName(child)}
                              </Typography>
                            }
                          />
                          {child.children && child.children.filter((gc) => gc.status !== 0).length > 0 && (
                            <Box
                              className="expand-icon"
                              sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(child.id);
                              }}
                            >
                              <Chip
                                label={child.children.filter((gc) => gc.status !== 0).length}
                                size="small"
                                sx={{
                                  bgcolor: "rgba(213, 170, 47, 0.08)",
                                  color: "primary.dark",
                                  fontWeight: 600,
                                  fontSize: "0.65rem",
                                  height: 20,
                                  pointerEvents: "none",
                                }}
                              />
                              {expanded[child.id] ? (
                                <ExpandLess sx={{ fontSize: 18, color: "primary.main", pointerEvents: "none" }} />
                              ) : (
                                <ExpandMore sx={{ fontSize: 18, color: "text.secondary", pointerEvents: "none" }} />
                              )}
                            </Box>
                          )}
                        </ListItemButton>
                      </ListItem>

                      {/* Grandchildren Categories */}
                      {child.children && child.children.filter((gc) => gc.status !== 0).length > 0 && (
                        <Collapse in={expanded[child.id]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {child.children.filter((gc) => gc.status !== 0).map((grand, gIdx) => (
                              <ListItem
                                key={grand.id}
                                disablePadding
                                sx={{
                                  pl: 6,
                                  mb: 0.5,
                                  animation: `fadeIn 0.25s ease-out ${gIdx * 0.05}s both`,
                                }}
                              >
                                <ListItemButton
                                  onClick={() => handleSelectCategory(grand.id, false)}
                                  sx={{
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.75,
                                    transition: "all 0.3s ease",
                                    bgcolor: selectedCatId === grand.id ? "rgba(213, 170, 47, 0.08)" : "transparent",
                                    borderLeft: selectedCatId === grand.id ? "2px solid" : "2px solid transparent",
                                    borderColor: selectedCatId === grand.id ? "primary.light" : "transparent",
                                    "&:hover": {
                                      bgcolor: "rgba(213, 170, 47, 0.04)",
                                      transform: "translateX(4px)",
                                    },
                                  }}
                                >
                                  <FiberManualRecordIcon
                                    sx={{
                                      fontSize: 6,
                                      color: "text.secondary",
                                      mr: 1,
                                    }}
                                  />
                                  <ListItemAvatar sx={{ minWidth: 40 }}>
                                    <Avatar
                                      src={getImageUrl(grand.image)}
                                      variant="rounded"
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        border: "1px solid rgba(213, 170, 47, 0.15)",
                                      }}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                                        {getName(grand)}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  ))}
                </List>
              </Collapse>
            )}

            <Divider sx={{ my: 1, mx: 2, borderColor: "rgba(213, 170, 47, 0.08)" }} />
          </Box>
        ))}
      </List>

      {/* Empty State */}
      {tree.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
          <CategoryIcon sx={{ fontSize: 48, color: "grey.300", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {lang === "ar" ? "لا توجد أقسام متاحة" : "No categories available"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CategoriesHorizontallyCompo;
